import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { ArrowLeft, ExternalLink, Phone, Plus, Reply, X } from 'lucide-react'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { useCreateTemplate, type TemplateButtonInput, type TemplateInput } from '../api/templates'
import { TemplatePreview } from '../components/TemplatePreview'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

// Meta's language codes for the languages a tenant in India is most likely to
// write in. Each language is reviewed and approved on its own.
const languages = [
  ['en', 'English'],
  ['en_US', 'English (US)'],
  ['en_GB', 'English (UK)'],
  ['hi', 'Hindi'],
  ['ta', 'Tamil'],
  ['te', 'Telugu'],
  ['kn', 'Kannada'],
  ['ml', 'Malayalam'],
  ['mr', 'Marathi'],
  ['bn', 'Bengali'],
  ['gu', 'Gujarati'],
] as const

type ButtonType = TemplateButtonInput['type']
type DraftButton = { key: number; type: ButtonType; text: string; url: string; urlExample: string; phoneNumber: string }

let nextKey = 0

// The highest {{n}} in the text: how many examples Meta needs.
function varCount(text: string): number {
  let max = 0
  for (const m of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) max = Math.max(max, Number(m[1]))
  return max
}

export function TemplateEditorPage() {
  const me = useMe()
  const create = useCreateTemplate()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [language, setLanguage] = useState('en')
  const [category, setCategory] = useState<TemplateInput['category']>('utility')
  const [header, setHeader] = useState('')
  const [headerExample, setHeaderExample] = useState('')
  const [body, setBody] = useState('')
  const [examples, setExamples] = useState<string[]>([])
  const [footer, setFooter] = useState('')
  const [buttons, setButtons] = useState<DraftButton[]>([])

  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'
  const n = varCount(body)
  const bodyExamples = Array.from({ length: n }, (_, i) => examples[i] ?? '')
  const headerHasVar = /\{\{\s*1\s*\}\}/.test(header)
  const error = create.error as RequestFailed | null
  const fieldError = (key: string) => error?.fields?.[key]

  if (me.data && !admin) {
    return (
      <main className="mx-auto flex max-w-2xl flex-col gap-4 px-4 py-8">
        <h1 className="text-2xl font-bold">New template</h1>
        <p className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">Only owners and admins can create templates.</p>
        <Button asChild variant="outline" className="self-start">
          <Link to="/templates">Back to templates</Link>
        </Button>
      </main>
    )
  }

  const addVariable = () => setBody((b) => `${b}${b && !b.endsWith(' ') ? ' ' : ''}{{${n + 1}}}`)
  const setButton = (key: number, change: Partial<DraftButton>) =>
    setButtons((bs) => bs.map((b) => (b.key === key ? { ...b, ...change } : b)))
  const addButton = (type: ButtonType) =>
    setButtons((bs) => [...bs, { key: nextKey++, type, text: '', url: 'https://', urlExample: '', phoneNumber: '' }])
  const count = (type: ButtonType) => buttons.filter((b) => b.type === type).length
  const full = buttons.length >= 3

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const input: TemplateInput = { name: name.trim(), language, category, body }
    if (header.trim()) input.header = header.trim()
    if (headerHasVar && headerExample.trim()) input.headerExample = headerExample.trim()
    if (n > 0) input.bodyExamples = bodyExamples.map((x) => x.trim())
    if (footer.trim()) input.footer = footer.trim()
    if (buttons.length > 0) {
      input.buttons = buttons.map((b) => {
        const out: TemplateButtonInput = { type: b.type, text: b.text.trim() }
        if (b.type === 'url') {
          out.url = b.url.trim()
          if (/\{\{\s*1\s*\}\}$/.test(b.url.trim()) && b.urlExample.trim()) out.urlExample = b.urlExample.trim()
        }
        if (b.type === 'phoneNumber') out.phoneNumber = b.phoneNumber.trim()
        return out
      })
    }
    create.mutate(input, { onSuccess: () => navigate('/templates') })
  }

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-6 sm:py-8">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="icon">
          <Link to="/templates" aria-label="Back to templates">
            <ArrowLeft aria-hidden="true" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">New template</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <form onSubmit={submit} className="flex min-w-0 flex-col gap-5" noValidate>
          {error && <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{error.message}</p>}

          <Section title="About this template">
            <Field id="name" label="Name" hint="Lowercase letters, digits and underscores. Customers never see it." error={fieldError('name')}>
              <Input
                id="name"
                value={name}
                maxLength={100}
                placeholder="order_update"
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="language" label="Language" error={fieldError('language')}>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger id="language" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {languages.map(([code, label]) => (
                      <SelectItem key={code} value={code}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                id="category"
                label="Category"
                hint={category === 'utility' ? 'Updates about something the customer asked for or bought.' : 'Offers, news and anything promotional. Meta charges more per message.'}
                error={fieldError('category')}
              >
                <Select value={category} onValueChange={(v) => setCategory(v === 'marketing' ? 'marketing' : 'utility')}>
                  <SelectTrigger id="category" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utility">Utility</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Message">
            <Field id="header" label="Header (optional)" hint="Bold first line, up to 60 characters. One variable, {{1}}, at most." error={fieldError('header')}>
              <Input id="header" value={header} maxLength={60} onChange={(e) => setHeader(e.target.value)} />
            </Field>
            {headerHasVar && (
              <Field id="header-example" label="Example for {{1}} in the header" error={fieldError('headerExample')}>
                <Input id="header-example" value={headerExample} maxLength={60} onChange={(e) => setHeaderExample(e.target.value)} />
              </Field>
            )}
            <Field id="body" label="Text" hint={`${body.length} of 1024 characters. Variables are filled in when the message is sent.`} error={fieldError('body')}>
              <Textarea id="body" rows={5} maxLength={1024} value={body} onChange={(e) => setBody(e.target.value)} />
            </Field>
            <Button type="button" variant="outline" size="sm" className="self-start" onClick={addVariable}>
              <Plus aria-hidden="true" />
              Add variable {`{{${n + 1}}}`}
            </Button>
            {n > 0 && (
              <fieldset className="flex flex-col gap-3 rounded-lg border p-3">
                <legend className="px-1 text-sm font-semibold">Examples for Meta's review</legend>
                <p className="text-xs text-muted-foreground">Real-looking values help approval. They are not sent to customers.</p>
                {bodyExamples.map((ex, i) => (
                  <Field key={i} id={`example-${i}`} label={`{{${i + 1}}}`}>
                    <Input
                      id={`example-${i}`}
                      value={ex}
                      maxLength={200}
                      onChange={(e) => setExamples(() => bodyExamples.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </Field>
                ))}
                {fieldError('bodyExamples') && <p className="text-sm text-destructive">{fieldError('bodyExamples')}</p>}
              </fieldset>
            )}
            <Field id="footer" label="Footer (optional)" hint="Small grey line, up to 60 characters, no variables." error={fieldError('footer')}>
              <Input id="footer" value={footer} maxLength={60} onChange={(e) => setFooter(e.target.value)} />
            </Field>
          </Section>

          <Section title="Buttons (optional)" hint="Up to 3. Keep quick replies together, before or after the link and call buttons.">
            {buttons.length > 0 && (
              <ol className="flex flex-col gap-3">
                {buttons.map((b, i) => (
                  <li key={b.key} className="flex flex-col gap-3 rounded-lg border p-3">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      {b.type === 'quickReply' ? 'Quick reply' : b.type === 'url' ? 'Website link' : 'Call button'}
                      <span className="flex-1" />
                      <Button type="button" variant="ghost" size="icon" aria-label={`Remove button ${i + 1}`} onClick={() => setButtons((bs) => bs.filter((x) => x.key !== b.key))}>
                        <X aria-hidden="true" />
                      </Button>
                    </div>
                    <Field id={`btn-text-${b.key}`} label="Label" hint="Up to 25 characters.">
                      <Input id={`btn-text-${b.key}`} value={b.text} maxLength={25} onChange={(e) => setButton(b.key, { text: e.target.value })} />
                    </Field>
                    {b.type === 'url' && (
                      <>
                        <Field id={`btn-url-${b.key}`} label="Website address" hint="Starts with https://. May end with {{1}} to add, for example, an order number.">
                          <Input id={`btn-url-${b.key}`} type="url" value={b.url} maxLength={2000} onChange={(e) => setButton(b.key, { url: e.target.value })} />
                        </Field>
                        {/\{\{\s*1\s*\}\}$/.test(b.url.trim()) && (
                          <Field id={`btn-ex-${b.key}`} label="Example for {{1}} in the address">
                            <Input id={`btn-ex-${b.key}`} value={b.urlExample} maxLength={200} onChange={(e) => setButton(b.key, { urlExample: e.target.value })} />
                          </Field>
                        )}
                      </>
                    )}
                    {b.type === 'phoneNumber' && (
                      <Field id={`btn-ph-${b.key}`} label="Phone number" hint="With country code, for example +918040001234.">
                        <Input id={`btn-ph-${b.key}`} type="tel" inputMode="tel" value={b.phoneNumber} maxLength={20} onChange={(e) => setButton(b.key, { phoneNumber: e.target.value })} />
                      </Field>
                    )}
                    {fieldError(`buttons.${i}`) && <p className="text-sm text-destructive">{fieldError(`buttons.${i}`)}</p>}
                  </li>
                ))}
              </ol>
            )}
            {fieldError('buttons') && <p className="text-sm text-destructive">{fieldError('buttons')}</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" disabled={full} onClick={() => addButton('quickReply')}>
                <Reply aria-hidden="true" />
                Quick reply
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={full || count('url') >= 2} onClick={() => addButton('url')}>
                <ExternalLink aria-hidden="true" />
                Website link
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={full || count('phoneNumber') >= 1} onClick={() => addButton('phoneNumber')}>
                <Phone aria-hidden="true" />
                Call button
              </Button>
            </div>
          </Section>

          <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t bg-background py-3">
            <p className="flex-[1_1_240px] text-xs text-muted-foreground">
              Submitting sends the template to Meta for review. Nothing is sent to customers and it costs nothing.
            </p>
            <Button asChild variant="ghost">
              <Link to="/templates">Cancel</Link>
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim() || !body.trim()}>
              {create.isPending ? 'Submitting' : 'Submit for review'}
            </Button>
          </div>
        </form>

        <aside aria-label="Preview" className="flex flex-col gap-2 lg:sticky lg:top-4 lg:self-start">
          <h2 className="text-sm font-semibold">Preview</h2>
          <TemplatePreview
            header={header.trim() || undefined}
            headerExample={headerExample}
            body={body}
            bodyExamples={bodyExamples}
            footer={footer.trim() || undefined}
            buttons={buttons.map((b) => ({ type: b.type, text: b.text }))}
          />
        </aside>
      </div>
    </main>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-xl border bg-card p-4 sm:p-5">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function Field({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: string | undefined; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className={cn('text-sm text-destructive')}>{error}</p>}
    </div>
  )
}
